import { useMemo } from 'react';
import { Alley } from '../../../../../services/entities/impl/blfApi/schemas/alley';
import {
  Upload,
  UploadSchema,
  UploadType,
} from '../../../../../services/entities/impl/blfApi/schemas/upload';
import { combineValidationResults } from '../../../utils/validation/combineValidationResults';
import {
  ValidationCounts,
  ValidationSummary,
  createValidationSummary,
} from '../../../utils/validation/createValidationSummary';
import { getValidationCounts } from '../../../utils/validation/getValidationCounts';
import {
  ValidationResult,
  validateSchema,
} from '../../../utils/validation/validateSchema';
import { validateUpload } from '../../utils/validateUpload';

/**
 * Hook for validating uploads data
 */
export const useUploadsValidation = (
  uploads: Upload[] | undefined,
  alleys: Alley[] | undefined
): {
  individualResults: Record<string, ValidationResult>;
  summary: ValidationSummary;
} => {
  return useMemo(() => {
    // Initialize with empty results
    const individualResults: Record<string, ValidationResult> = {};

    // Check for empty uploads data
    if (!uploads) {
      const emptyValidationResult: ValidationResult = {
        valid: false,
        messages: [
          {
            severity: 'error',
            path: '',
            message: 'No uploads data found. API endpoint may have failed.',
          },
        ],
      };

      const counts = getValidationCounts(emptyValidationResult);

      return {
        individualResults,
        summary: {
          result: createValidationSummary(counts),
          counts,
        },
      };
    }

    // Check for empty array
    if (uploads.length === 0) {
      const emptyValidationResult: ValidationResult = {
        valid: false,
        messages: [
          {
            severity: 'error',
            path: '',
            message: 'No uploads found. Expected at least one upload.',
          },
        ],
      };

      const counts = getValidationCounts(emptyValidationResult);

      return {
        individualResults,
        summary: {
          result: createValidationSummary(counts),
          counts,
        },
      };
    }

    // Check for multiple collection.json files
    const collectionJsonUploads = uploads.filter(
      (upload) => upload.type === UploadType.COLLECTION_JSON
    );
    const hasMultipleCollectionJson = collectionJsonUploads.length > 1;

    // Track total counts
    let totalErrors = 0;
    let totalWarnings = 0;

    // Validate each upload
    uploads.forEach((upload) => {
      // 1. Validate against schema
      const schemaResult = validateSchema(upload, UploadSchema);

      // 2. Perform cross-entity validation with alleys
      const crossEntityResult = validateUpload(upload, alleys || []);

      // 3. Create a new result object to hold any additional custom validations
      const additionalValidations: ValidationResult = {
        valid: true,
        messages: [],
      };

      // Add warning for collection.json files if multiple are found
      if (
        upload.type === UploadType.COLLECTION_JSON &&
        hasMultipleCollectionJson
      ) {
        additionalValidations.messages.push({
          severity: 'warning',
          path: 'type',
          message: `Multiple collection.json files detected (${collectionJsonUploads.length}). Only one is expected.`,
        });
        additionalValidations.valid = false;
      }

      // 4. Combine all validation results
      const validationResult = combineValidationResults(
        schemaResult,
        crossEntityResult,
        additionalValidations
      );

      // 5. Store result for this upload
      individualResults[upload.id] = validationResult;

      // 6. Update totals
      const counts = getValidationCounts(validationResult);
      totalErrors += counts.errors;
      totalWarnings += counts.warnings;
    });

    // Create summary counts
    const summaryCounts: ValidationCounts = {
      errors: totalErrors,
      warnings: totalWarnings,
      total: totalErrors + totalWarnings,
    };

    // Create summary result with messages
    const summaryResult = createValidationSummary(summaryCounts);

    return {
      individualResults,
      summary: {
        result: summaryResult,
        counts: summaryCounts,
      },
    };
  }, [uploads, alleys]);
};
